import { LocationNumbersService } from '../../../services'

const fetchPhoneNumbers = async function (
  { commit, state, getters, dispatch, rootState },
  config,
) {
  let tableConfig

  const { companyId, locationId, payload, getPaginatedDataOnly = false, locationTwilioId } = config
  const twilioAccountId = locationTwilioId ?? state.locationTwilio?.id ?? rootState?.twilioAccount?.twilioAccount?.id
  const isUnderGHL = state.locationTwilio?.underGHLAccount ?? state.locationTwilio?.under_ghl_account
  /**
   * If payload is not provided, then we are fetching
   * the phone numbers from the state table
   */
  if (!payload) {
    tableConfig = {
      limit: state.table?.limit,
      page: state.table?.currentPage,
      query: state.table?.searchText ?? '',
    }
  }

  const table = payload ?? tableConfig

  if (getPaginatedDataOnly) {
    const result = await dispatch('fetchLocalPhoneNumbers', {
      paginationConfig: table,
    })
    return Promise.resolve(result)
  }

  commit('startLoading')
  try {
    const data = await LocationNumbersService.getInboundPhoneNumbers(
      locationId,
      twilioAccountId
    )

    if (data && data.incoming_phone_numbers) {
      if(isUnderGHL) {
        try {
          await dispatch('fetchPhoneNumbersA2PStatus', {locationId, companyId})
        } catch(error) {
          console.error(error)
        }
      }

      commit(
        'setPhoneNumbers',
        getters.getCorrectedPhoneNumbers(data.incoming_phone_numbers)
        )
      commit('setPhoneNumbersLookupTable', 'phone_number')
      commit('setTableRecordsCount', data.incoming_phone_numbers?.length)
      commit('setTable', payload ?? tableConfig)

      const result = getters.getPaginatedPhoneNumbers
      return Promise.resolve({ incoming_phone_numbers: result })
    }

    commit('setPhoneNumbers', [])
    return Promise.resolve({ incoming_phone_numbers: [] })
  } catch (error) {
    return Promise.reject(error)
  } finally {
    commit('stopLoading')
  }
}

const fetchPhoneNumbersFromDBPaginated = async function (
{ commit, state, getters, dispatch, rootState },
config,
) {
let tableConfig

const { companyId, locationId, payload, locationTwilioId } = config

const isUnderGHL = state.locationTwilio?.underGHLAccount ?? state.locationTwilio?.under_ghl_account
/**
 * If payload is not provided, then we are fetching
 * the phone numbers from the state table
 */
if (!payload) {
  tableConfig = {
    limit: state.table?.limit,
    page: state.table?.currentPage,
    query: state.table?.searchText ?? '',
  }
}

commit('startLoading')
try {
  // Store the previous search text before any updates
  const previousSearchText = state.table?.searchText

  if (payload.page === 1) {
    const twilioAccountId = locationTwilioId ?? state.locationTwilio?.id ?? rootState?.twilioAccount?.twilioAccount?.id

    LocationNumbersService.getInboundPhoneNumbers(
      locationId,
      twilioAccountId
    ).then((data) => {
      commit(
        'setPhoneNumbers',
        getters.getCorrectedPhoneNumbers(data.incoming_phone_numbers, false)
      )
      commit('setPhoneNumbersLookupTable', 'phone_number')
    })

    commit('setTable', payload ?? tableConfig)
  }

  let data;
  const hasUserChangedSearchText: boolean = previousSearchText !== payload?.query
  if (hasUserChangedSearchText || !state.phoneNumbersPaginated[payload.page ?? 1]) {

    data = await LocationNumbersService.getInboundPhoneNumbersFromDB(
      locationId,
      payload ?? tableConfig
    )

    data.incoming_phone_numbers = data.numbers
    data.page = data.page + 1

    // rename key .phoneNumber .phone_number inside data.incoming_phone_numbers
    data.incoming_phone_numbers.forEach((item) => {
      item.phone_number = item.phoneNumber
      delete item.phoneNumber
    })

    // rename key .friendlyName .friendly_name inside data.incoming_phone_numbers
    data.incoming_phone_numbers.forEach((item) => {
      item.friendly_name = item.friendlyName
      delete item.friendlyName
    })
  } else {
    data = {
      incoming_phone_numbers: state.phoneNumbersPaginated[payload.page ?? 1],
      page: payload.page ?? 1,
      isUnderGhl: state.phoneNumbersIsUnderGHL,
      total: state.phoneNumbersTotalPages
    }
  }

  if (data && (data.numbers || data.incoming_phone_numbers)) {
    commit('setPhoneNumbersPaginated', {phoneNumbers: data.incoming_phone_numbers, page: data.page, isUnderGHL: data.isUnderGhl, totalPages: data.total})

    commit('setPhoneNumbersLookupTable', 'phone_number')

    commit('setTableRecordsCount', data.total)

    commit('setTable', payload ?? tableConfig)

    let totalRecords: number = data.total
    if(isUnderGHL) {
      dispatch('fetchPhoneNumbersA2PStatus', {locationId, companyId}).then((linkedNumbers) => {
        commit('setPhoneNumbersPaginated', {phoneNumbers: getters.getCorrectedPhoneNumbers(data.incoming_phone_numbers, true), page: data.page, isUnderGhl: data.isUnderGhl, totalPages: data.total})

        commit('setPhoneNumbersLookupTable', 'phone_number')

        commit('setTableRecordsCount', data.total)

        commit('setTable', payload ?? tableConfig)
      }).catch(error => {
        console.error('debug: Error fetching A2P status', error)
      });
    } else {
      const twilioAccountId = locationTwilioId ?? state.locationTwilio?.id ?? rootState?.twilioAccount?.twilioAccount?.id
      const twilioData = await LocationNumbersService.getInboundPhoneNumbers(
        locationId,
        twilioAccountId
      )
      let phoneNumbers = getters.getCorrectedPhoneNumbers(twilioData.incoming_phone_numbers || [], false)
      
      // apply search filter to phoneNumbers
      if (payload?.query) {
        phoneNumbers = phoneNumbers.filter((item) => item.phone_number.includes(payload.query))
      }

      commit(
        'setPhoneNumbers',
        phoneNumbers
      )

      // transform phoneNumbers to be compatible with the phoneNumbersPaginated
      const currentPage = state.table?.currentPage
      const pageSize = state.table?.limit
      const phoneNumbersPaginated = phoneNumbers.slice((currentPage - 1) * pageSize, currentPage * pageSize)

      commit('setPhoneNumbersPaginated', {phoneNumbers: phoneNumbersPaginated, page: currentPage, isUnderGhl: twilioData.isUnderGhl, totalPages: twilioData.incoming_phone_numbers?.length || 0})
      commit('setPhoneNumbersLookupTable', 'phone_number')
      commit('setTableRecordsCount', twilioData.incoming_phone_numbers?.length || 0)
      commit('setTable', payload ?? tableConfig)

      totalRecords = twilioData.incoming_phone_numbers?.length || 0
    }

    return Promise.resolve({ incoming_phone_numbers: state.phoneNumbersPaginated[payload.page ?? 1], totalRecords: totalRecords })
  }

  if (state?.phoneNumbers?.length === 0) {
    commit('setPhoneNumbers', getters.getCorrectedPhoneNumbers(data.incoming_phone_numbers))
  }
  return Promise.resolve({ incoming_phone_numbers: [], totalRecords: 0 })
} catch (error) {
  return Promise.reject(error)
} finally {
  commit('stopLoading')
}
}

export { fetchPhoneNumbers, fetchPhoneNumbersFromDBPaginated }
